import { fetchAuthSession } from "@aws-amplify/core";
import { LambdaClient, InvokeCommand, InvokeCommandOutput } from "@aws-sdk/client-lambda";
import {
	Button,
	Category,
	ConfirmationModal,
	DialogContainer,
	Modal,
	ModalFooter,
	ModalHeading,
	ModalInputBox,
	ModalTitle,
	ModalTitleContainer,
	ModelSubHeading,
	OptionsDropdown,
	PercentValue,
	ProgressMetadata,
	TextFieldWrapper,
	Typography,
} from "@factorearth/component-library";
import { Colors } from "@factorearth/component-library/dist/Theme/types";
import { SelectAutoSyncTemplates } from "components/CreateNewProject/SelectAutoSyncTemplates";
import UploadFileButton from "components/UploadFileButton/UploadFileButton";
import { FormGroup, FormGroupForResolver, FormInstance, FormTemplateForResolver, Project, Source, SyncConfig, UserForResolver } from "db/definitions";
import { syncTemplateWarningMessage } from "utils/syncTemplateWarningMessage";
import {
	downloadFormGroupJson,
	duplicateJsonStructure,
	mergeAndSave,
	prepDbForMerge,
	prepJsonForMerge,
	saveJsonDirectlyToDb,
	addAllFormTemplatesToMergeData,
} from "db/merge";
import { Location } from "hooks/useDrawer";
import React, { useEffect, useRef, useState } from "react";
import { FaSortAmountDown, FaSortAmountUpAlt } from "react-icons/fa";
import {
	FiCalendar,
	FiChevronRight,
	FiCopy,
	FiDownload,
	FiEdit,
	FiFileText,
	FiFilter,
	FiImage,
	FiList,
	FiMapPin,
	FiMoreVertical,
	FiPlus,
	FiTrash2,
	FiUpload,
	FiUploadCloud,
	FiX,
} from "react-icons/fi";
import { useNavigate } from "react-router";
import { divideInto2Arrays } from "utils/divideInto2Arrays";
import { duplicateFormGroups } from "utils/duplicateFormGroups";
import handleProjectUpdate from "utils/handleProjectUpdate";
import handleSetOrRemoveAutoSyncFormTemplates from "utils/handleSetAutoSyncFormTemplates";
import handleUploadFromCsv from "utils/handleUploadFromCsv";
import { RecentProject, updateRecentProjects } from "utils/localstorage/projects";
import { recordSentry } from "utils/recordSentry";
import styles from "./FormGroupListOptions.module.scss";
import { checkIfTemplatesSaved } from "db/queries/formTemplateQueries";
import { uploadData } from "aws-amplify/storage";
import SavedLocally from "assets/icons/SavedLocallyIcon";
import { getProjectAndFormGroupsForSyncTab } from "db/queries/projectQueries";
import { useDownloadImagesModal } from "hooks/useDownloadImageModal";
import { useAnalytics } from "hooks/useAnalytics";
import { ChangePreview } from "services/gisUpload/types";
import Svg from "components/Svg/Svg";
import CsvUploadModal from "./DevOptions/CsvUploadModal/CsvUploadModal";

interface Props {
	color: Colors;
	deleteProject: (project: Project) => Promise<void>;
	updateProject?: (project: Project) => Promise<void>;
	deleteSelectedFormGroups: (selectedFormGroups: string[]) => Promise<void>;
	fetchFormGroupsForState: (ids: string[]) => Promise<void>;
	formGroups: FormGroup[];
	getProject: (id: string) => Promise<Project | null>;
	handleCreateNewClick: () => void;
	handleFilterOption: (option: "All" | "Default" | "Photo Logs" | "") => void;
	filterOption: "All" | "Default" | "Photo Logs" | "";
	online: boolean;
	openDropdownId: number | null;
	pageIndex: number;
	pageSize: number;
	project: Project;
	query: string;
	setupDrawer: (location: Location | "") => void;
	selected: string[];
	setDevFormGroupOptionsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	setForceUpdate: React.Dispatch<React.SetStateAction<boolean>>;
	setNumSort: React.Dispatch<React.SetStateAction<{ ascending: boolean; enabled: boolean }>>;
	numSort: Record<string, boolean>;
	setPageIndex: React.Dispatch<React.SetStateAction<number>>;
	setOpenDropdownId: React.Dispatch<React.SetStateAction<number | null>>;
	setQuery: React.Dispatch<React.SetStateAction<string>>;
	setSelected: React.Dispatch<React.SetStateAction<string[]>>;
	user: UserForResolver | null | undefined;
	formTemplates?: FormTemplateForResolver[];
	addFormGroupsInState: (formGroups: FormGroup[]) => void;
	updateProjectsSyncMenu: (project: Project) => void;
	persisted?: boolean;
	setProgress: (newProgress: Partial<ProgressMetadata>) => void;
	handleGISCsvUpload: (file: File) => void;
	handleApplyCsvImport: (preview: ChangePreview | null) => Promise<void>;
	csvPreview: ChangePreview | null;
}

function formatBytes(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FormGroupListOptions(props: Props) {
	const {
		color,
		deleteProject,
		deleteSelectedFormGroups,
		fetchFormGroupsForState,
		formGroups,
		getProject,
		handleCreateNewClick,
		handleFilterOption,
		filterOption,
		online,
		pageIndex,
		pageSize,
		project,
		query,
		selected,
		setNumSort,
		numSort,
		setPageIndex,
		setOpenDropdownId,
		setQuery,
		setSelected,
		user,
		updateProject,
		updateProjectsSyncMenu,
		addFormGroupsInState,
		setForceUpdate,
		formTemplates = [],
		persisted,
		setProgress,
		handleGISCsvUpload,
		handleApplyCsvImport,
		csvPreview,
	} = props;
	const [duplicateInProgress, setDuplicateInProgress] = useState<boolean>(false);
	const [editProjectOpen, setEditProjectOpen] = useState<boolean>(false);
	const [deleteProjectOpen, setDeleteProjectOpen] = useState<boolean>(false);
	const [uploadJSONProjectOpen, setUploadJSONProjectOpen] = useState<boolean>(false);
	const [uploadCSVProjectOpen, setUploadCSVProjectOpen] = useState<boolean>(false);
	const [uploadGISCsvProjectOpen, setUploadGISCsvProjectOpen] = useState<boolean>(false);
	const [deleteFormGroup, setDeleteFormGroup] = useState(false);
	const [offlineProject, setOfflineProject] = useState<Project | null>(null);
	const [offlineFormGroups, setOfflineFormGroups] = useState<FormGroup[]>([]);
	const navigate = useNavigate();

	const { trackUserBehaviorEvent } = useAnalytics({
		userId: user?.id || "",
		groupId: user?.group || "",
		online,
	});

	const [DownloadImagesModal, showDownloadImagesModal] = useDownloadImagesModal();
	/** True while the toolbar sync (push then pull) is running; blocks re-entry from a second click. */
	const syncInProgressRef = useRef(false);
	// Rendered so tests can wait for the whole click. The ref alone does not re-render.
	const [syncRunning, setSyncRunning] = useState(false);

	/**
	 * Reads the project as it exists on this device (IndexedDB) into state and returns it. Called on mount, and again
	 * right before a push: a pull on this same page creates or changes the device copy, and a push that still used
	 * the mount-time value (often null) would silently send nothing.
	 */
	async function loadOfflineProject(): Promise<Project | null> {
		const result = project.id ? await getProjectAndFormGroupsForSyncTab(project.id) : null;
		if (result) {
			setOfflineFormGroups(result.formGroups?.items || []);
			setOfflineProject(result);
			return result;
		}
		setOfflineFormGroups([]);
		setOfflineProject(null);
		return null;
	}

	useEffect(() => {
		if (project.id) {
			loadOfflineProject();
		}
	}, [project.id]);

	/**
	 * Downloads all selected formGroups and its child instances (NOT TEMPLATES) into a json file, named with their username and timestamp
	 */
	async function downloadSelectedFormGroupsAndBackup(selected: FormGroup[], localProject: Project | null = offlineProject) {
		// Close the options menu
		try {
			// Get the formGroups
			const syncConfig: SyncConfig = {
				online,
				source: "local",
				destination: "json",
			};

			const projectToUse = localProject || project;
			if (projectToUse) {
				const selectedFormGroupIds = selected.map((fg) => fg.id);
				const jsonStructure = await downloadFormGroupJson(projectToUse, selectedFormGroupIds, syncConfig);

				// const stringifiedJson = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(jsonStructure))}`;
				const stringifiedJson = JSON.stringify(jsonStructure);

				// Create download anchor + download
				const date = new Date();
				const year = date.getFullYear().toString().slice(-2); // Last 2 digits of the year
				const month = (date.getMonth() + 1).toString().padStart(2, "0"); // The month with a leading zero if less than 2 characters
				const day = date.getDate().toString().padStart(2, "0"); // The day with a leading 0 if it's less than 2 characters
				const hour = date.getHours().toString().padStart(2, "0"); // The hour with a leading 0 if less than 2 characters
				const minute = date.getMinutes().toString().padStart(2, "0"); // The minutes with a leading 0 if less than 2 characters
				const seconds = date.getSeconds().toString().padStart(2, "0"); // The seconds with a leading 0 if less than 2 characters
				const finalTime = `${hour}${minute}${seconds} ${month}-${day}-${year}`;
				// downloadAnchorNode.setAttribute("download", `${finalTime}|${project.name}|${user?.username || "UnknownUser"}.json`);
				const url = `${user?.group}/sync-backups/${projectToUse.name}/${user?.username || "UnknownUser"}-${finalTime}.json`;
				try {
					await uploadData({
						key: url,
						// Alternatively, path: ({identityId}) => `protected/${identityId}/album/2024/1.jpg`
						data: stringifiedJson,
					}).result;
				} catch (error) {
					const message = "ERROR SAVING JSON TO S3 BUCKET";
					recordSentry({ error, msg: message });
				}
			}
		} catch (err) {
			const message = "ERROR DOWNLOADING JSON TO SAVE TO S3";
			recordSentry({ error: err, msg: message });
		}
	}

	/**
	 * Given a sync config, handles fetching the relevant stuff and merging them to the correct db
	 * @author Eric Webb <ewebb@factorearth.com>
	 * @param syncConfig The sync config
	 * @returns Everything from the merge process so we can rebuild formGroups as necessary
	 */
	async function mergeLocalOrRemoteToOpposite(syncConfig: SyncConfig, currentProgress?: PercentValue, localProject: Project | null = offlineProject) {
		if (selected.length > 0 && (project || localProject) && user) {
			const oppositeMap: Record<Source, Source> = {
				local: "remote",
				remote: "local",
				json: "json", // Makes TS happy, not actually configured yet
			};
			// Prep the progress value, if it exists add to it, otherwise start at 0.
			// this variable is being used because we needed to track the progress across multiple methods.
			// it is setup as a variable rather than using the original variable so we can mutate that value and make sure it exists, also its a bit more readable this way imo.
			let progressValue: PercentValue = (currentProgress || 0) as PercentValue;
			const forceUpdateData = `REFRESH_${Date.now()}`;
			const sourceProjCopy: Project | undefined =
				syncConfig.source === "local"
					? localProject
						? { ...localProject, forceUpdateData }
						: undefined
					: project
						? { ...project, forceUpdateData }
						: undefined;
			const destinationProjCopy: Project | undefined =
				syncConfig.destination === "remote"
					? project
						? { ...project, forceUpdateData }
						: undefined
					: localProject
						? { ...localProject, forceUpdateData }
						: undefined;
			if (sourceProjCopy) {
				try {
					progressValue = (progressValue + 10) as PercentValue;
					setProgress({
						value: progressValue,
						variant: "info",
						message: `Preparing ${syncConfig.source} database for merge`,
						title: "Syncing FormGroups",
						canClose: false,
						show: true,
						// useProgress only restarts its hide timer when `timeout` changes. Omit it and a
						// previous success banner's 5s timer stays armed and hides this update mid-sync.
						timeout: undefined,
					});
					const donorData = await prepDbForMerge(user, syncConfig, selected, sourceProjCopy);
					if (donorData.imagesCouldBeDownloaded) {
						syncConfig.savePhotos = !!(await showDownloadImagesModal());
					}
					progressValue = (progressValue + 10) as PercentValue;
					setProgress({
						value: progressValue,
						variant: "info",
						message: `Preparing ${syncConfig.destination} database for merge`,
						title: "Syncing FormGroups",
						canClose: false,
						show: true,
						timeout: undefined,
					});
					const destinationSyncConfig: SyncConfig = {
						online,
						source: oppositeMap[syncConfig.source],
						destination: oppositeMap[syncConfig.destination],
					};
					const receiverData = await prepDbForMerge(user, destinationSyncConfig, selected, destinationProjCopy);
					// Enhance receiverData with all available form templates (only if newer than donorData)
					await addAllFormTemplatesToMergeData(receiverData, donorData, destinationSyncConfig.source === "remote");
					progressValue = (progressValue + 10) as PercentValue;
					setProgress({
						value: progressValue,
						variant: "info",
						message: `Merging ${syncConfig.source} data to ${syncConfig.destination} database`,
						title: "Syncing FormGroups",
						canClose: false,
						show: true,
						timeout: undefined,
					});
					const mergeDataToSave = await mergeAndSave(donorData, receiverData, syncConfig, { username: user?.username });
					return {
						mergeDataToSave,
						donorData,
						receiverData,
						currentProgress: progressValue,
					};
				} catch (error) {
					// mergeAndSave refuses to merge when the templates for a form cannot be found on either side. Returning
					// undefined lets the callers show their "Failed to sync" state instead of leaving the progress modal stuck.
					recordSentry({ error, msg: `Sync ${syncConfig.source} -> ${syncConfig.destination} failed` });
					return undefined;
				}
			}
		}
	}

	/**
	 * Takes the selected formGroups and saves them offline
	 * @author Eric Webb <ewebb@factorearth.com>
	 */
	async function getFormGroups(currentProgress?: PercentValue) {
		if (selected.length > 0 && (project || offlineProject)) {
			const syncConfig: SyncConfig = {
				online,
				source: "remote",
				destination: "local",
			};
			let progressValue = (currentProgress !== undefined ? currentProgress : 0) as PercentValue;
			setProgress({
				value: progressValue,
				variant: "info",
				message: "Preparing to merge...",
				title: "Syncing FormGroups for Offline Editing",
				canClose: false,
				show: true,
				timeout: undefined, // undefined so it doesnt close automatically?
			});
			// Merge into the device copy as it is right now (the push that usually precedes this pull just rewrote it).
			const localProject = await loadOfflineProject();
			const result = await mergeLocalOrRemoteToOpposite(syncConfig, progressValue, localProject);
			if (result) {
				progressValue = result.currentProgress !== undefined ? result.currentProgress : 80;
				progressValue = (progressValue + 10) as PercentValue;
				setProgress({
					value: progressValue,
					variant: "info",
					message: "Updating State...",
					title: "Syncing FormGroups for Offline Editing",
					canClose: false,
					show: true,
					timeout: undefined,
				});
				const { mergeDataToSave, donorData } = result;
				const offlineCopy = [...offlineFormGroups];
				mergeDataToSave.formGroups.forEach((formGroup) => {
					const fgCopy: FormGroup = { ...formGroup };
					const formInstances = [];
					for (const formId of fgCopy.formOrder) {
						// Get instance
						const formInstance = donorData.formInstances.get(formId);
						if (formInstance) {
							const formCopy: FormInstance = { ...formInstance, fieldInstances: { items: [] } };
							const formTemplate = donorData.formTemplates.get(formCopy.formTemplateId);
							if (formTemplate) {
								const formTemplateCopy = { ...formTemplate };
								formCopy.formTemplate = formTemplateCopy;
							}
							formInstances.push(formCopy);
						}
					}
					fgCopy.formInstances = { items: formInstances };
					const foundIndex = offlineCopy.findIndex((fg) => fg.id === fgCopy.id);
					if (foundIndex !== -1) {
						offlineCopy[foundIndex] = fgCopy;
					} else {
						offlineCopy.push(fgCopy);
					}
				});
				setOfflineFormGroups(offlineCopy);
				// BE AWARE POTENTIAL FUTURE DEVELOPER --------
				// At this point this is the final step so im just gonna set to 100.
				// it will either be successful or fail here until someone changes this process.
				progressValue = 100 as PercentValue;
				setProgress({
					value: progressValue,
					variant: mergeDataToSave.templateWarnings?.length ? "info" : "success",
					message: syncTemplateWarningMessage("FormGroups Successfully Synced Offline", mergeDataToSave.templateWarnings),
					title: "FormGroups Successfully Synced Offline",
					timeout: mergeDataToSave.templateWarnings?.length ? undefined : 5000,
					canClose: true,
					show: true,
				});
				await checkIfTemplatesSaved();
			} else {
				setProgress({
					value: 100,
					variant: "destructive",
					message: "Failed to sync FormGroups for Offline Editing",
					title: "Failed to sync FormGroups for Offline Editing",
					timeout: 5000,
					canClose: true,
					show: true,
				});
			}
		}
	}

	async function mergeButton(currentProgress: PercentValue | undefined): Promise<PercentValue | undefined> {
		let progressValue = currentProgress !== undefined ? currentProgress : 0;
		if (selected.length > 0 && (project || offlineProject)) {
			const syncConfig: SyncConfig = {
				online,
				source: "local",
				destination: "remote",
			};
			setProgress({
				value: progressValue,
				variant: "info",
				message: "Preparing to merge...",
				title: "Syncing Offline FormGroups to Online",
				canClose: false,
				show: true,
				timeout: undefined,
			});
			// Push what is on the device right now, not what was there when the page mounted (a pull since then may
			// have created or changed it).
			const localProject = await loadOfflineProject();
			// The local snapshot must be on S3 before the merge writes anything; the function never rejects.
			if (localProject) await downloadSelectedFormGroupsAndBackup(localProject.formGroups.items, localProject);
			const result = await mergeLocalOrRemoteToOpposite(syncConfig, progressValue, localProject);

			if (result) {
				const { mergeDataToSave, donorData } = result;
				// increment progressValue if it is in use, otherwise just set to 80.
				progressValue = result.currentProgress !== undefined ? result.currentProgress : 80;
				setProgress({
					value: progressValue,
					variant: "info",
					message: "Updating State...",
					title: "Syncing Offline FormGroups to Online",
					canClose: false,
					show: true,
					timeout: undefined,
				});
				const onlineCopy = [...formGroups];
				mergeDataToSave.formGroups.forEach((formGroup) => {
					const fgCopy: FormGroup = { ...formGroup };
					const formInstances = [];
					for (const formId of fgCopy.formOrder) {
						// Get instance
						const formInstance = donorData.formInstances.get(formId);
						if (formInstance) {
							const formCopy: FormInstance = { ...formInstance, fieldInstances: { items: [] } };
							const formTemplate = donorData.formTemplates.get(formCopy.formTemplateId);
							if (formTemplate) {
								const formTemplateCopy = { ...formTemplate };
								formCopy.formTemplate = formTemplateCopy;
							}
							formInstances.push(formCopy);
						}
					}
					fgCopy.formInstances = { items: formInstances };
					const foundIndex = onlineCopy.findIndex((fg) => fg.id === fgCopy.id);
					if (foundIndex !== -1) {
						onlineCopy[foundIndex] = fgCopy;
					} else {
						onlineCopy.push(fgCopy);
					}
				});
				addFormGroupsInState(onlineCopy);
				const projectCopy = { ...mergeDataToSave.projects[0], formGroups: { items: onlineCopy } };
				updateProjectsSyncMenu(projectCopy);
				// Increment progressValue if it is in use, otherwise just set to 100.
				progressValue = result.currentProgress !== undefined ? ((progressValue + 10) as PercentValue) : (100 as PercentValue);
				setProgress({
					value: progressValue,
					variant: mergeDataToSave.templateWarnings?.length ? "info" : "success",
					message: syncTemplateWarningMessage("FormGroups Successfully Synced Online", mergeDataToSave.templateWarnings),
					title: "FormGroups Successfully Synced Online",
					// The pull starts immediately after this. A 5s timeout here is not cleared by the
					// pull's updates, so the bar disappears while sync is still running.
					timeout: undefined,
					canClose: false,
					show: true,
				});
			} else {
				progressValue = 100;
				setProgress({
					value: progressValue,
					variant: "destructive",
					message: "Failed to sync Offline FormGroups to Online. Nothing was changed.",
					title: "Failed to sync Offline FormGroups to Online",
					timeout: undefined,
					canClose: false,
					show: true,
				});
			}
		} else {
			// No form groups to work with so should just set to 100 so the progress bar fills.
			progressValue = 100;
			setProgress({
				value: progressValue,
				variant: "info",
				message: "No form groups to process.",
				title: "No form groups to process.",
				timeout: 5000,
				canClose: true,
				show: true,
			});
		}
		return progressValue;
	}

	async function syncButtonClick() {
		// Same guard as SyncProject.mergeButton: a second click while a sync is running (the backup upload and the
		// merge both take a while) would start a second merge and upload the same images twice.
		if (syncInProgressRef.current) return;
		syncInProgressRef.current = true;
		setSyncRunning(true);
		try {
			let currentProgress: PercentValue | undefined = 0;
			currentProgress = await mergeButton(currentProgress);
			await getFormGroups(currentProgress);
			setForceUpdate((prev) => !prev);
		} finally {
			syncInProgressRef.current = false;
			setSyncRunning(false);
		}
	}

	let optionsObj: Record<string, Category> = {
		Sort: {
			icon: !numSort.enabled ? <FiCalendar /> : numSort.enabled && numSort.ascending ? <FaSortAmountUpAlt /> : <FaSortAmountDown />,
			title: !numSort.enabled ? "Date" : numSort.enabled && numSort.ascending ? "A-Z" : "Z-A",
			options: [
				{
					name: "A-Z Ascending",
					onClick: () =>
						setNumSort({
							ascending: true,
							enabled: true,
						}),
					icon: <FaSortAmountUpAlt />,
				},
				{
					name: "Z-A Descending",
					onClick: () =>
						setNumSort({
							ascending: false,
							enabled: true,
						}),
					icon: <FaSortAmountDown />,
				},
				{
					name: "Date Created",
					onClick: () =>
						setNumSort({
							ascending: true,
							enabled: false,
						}),
					icon: <FiCalendar />,
				},
			],
		},
		Filter: {
			icon: filterOption === "Photo Logs" ? <FiImage /> : filterOption === "Default" ? <FiFileText /> : <FiFilter />,
			title: filterOption === "Photo Logs" ? "Photo Logs" : filterOption === "Default" ? "Default" : "Filter",
			options: [
				{
					name: "Photo Logs",
					onClick: () => {
						handleFilterOption("Photo Logs");
					},
					icon: <FiImage />,
				},
				{
					name: "Default",
					onClick: () => {
						handleFilterOption("Default");
					},
					icon: <FiFileText />,
				},
				{
					name: "All",
					onClick: () => {
						handleFilterOption("All");
					},
					icon: <FiList />,
				},
			],
		},
		Option: {
			icon: <FiMoreVertical />,
			dataTestId: "project-options",
			options: [
				{
					name: "Upload JSON",
					onClick: () => {
						setUploadJSONProjectOpen(!uploadJSONProjectOpen);
					},
					icon: <FiUploadCloud />,
				},
				{
					name: "Manage Project",
					onClick: () => {
						setEditProjectOpen(true);
					},
					icon: <FiEdit />,
					dataTestId: "edit-project",
				},
				{
					name: "Upload GIS CSV",
					onClick: () => {
						setUploadGISCsvProjectOpen(!uploadGISCsvProjectOpen);
					},
					icon: <FiMapPin />,
					disabled: selected.length === 0,
				},
				// {
				// 	name: "Delete Project",
				// 	onClick: () => {
				// 		setDeleteProjectOpen(true);
				// 	},
				// 	icon: <FiTrash2 />,
				// 	dataTestId: "delete-project",
				// 	variant: "destructive",
				// },
				{
					name: "Duplicate",
					onClick: () => duplicateSelectedFormGroups(),
					icon: <FiCopy />,
					disabled: selected.length === 0,
				},
				{
					name: "Download",
					onClick: () => downloadSelectedFormGroups(),
					icon: <FiDownload />,
					disabled: selected.length === 0,
				},
				{
					name: "Delete",
					onClick: () => setDeleteFormGroup(true),
					icon: <FiTrash2 />,
					disabled: selected.length === 0,
					variant: "destructive",
				},
			],
		},
	};
	if (online && persisted) {
		optionsObj.Option = {
			icon: <FiMoreVertical />,
			dataTestId: "project-options",
			options: [
				{
					name: "Upload JSON",
					onClick: () => {
						setUploadJSONProjectOpen(!uploadJSONProjectOpen);
					},
					icon: <FiUploadCloud />,
				},

				// {
				// 	name: "Upload CSV",
				// 	onClick: () => {
				// 		setUploadCSVProjectOpen(!uploadCSVProjectOpen);
				// 	},
				// 	icon: <FiUpload />,
				// 	dataTestId: "csv-upload",
				// },
				{
					name: "Manage Project",
					onClick: () => {
						setEditProjectOpen(true);
					},
					icon: <FiEdit />,
					dataTestId: "edit-project",
				},
				{
					name: "Upload GIS CSV",
					onClick: () => {
						setUploadGISCsvProjectOpen(!uploadGISCsvProjectOpen);
					},
					icon: <FiMapPin />,
					disabled: selected.length === 0,
				},
				// {
				// 	name: "Delete Project",
				// 	onClick: () => {
				// 		setDeleteProjectOpen(true);
				// 	},
				// 	icon: <FiTrash2 />,
				// 	dataTestId: "delete-project",
				// 	variant: "destructive",
				// },
				{
					name: "Sync",
					onClick: syncButtonClick,
					icon: <SavedLocally color={color.background.primary} width={16} height={16} style={{ margin: "none" }} />,
					disabled: selected.length === 0,
					dataTestId: "sync-button",
				},
				{
					name: "Export",
					onClick: exportSelectedFormGroups,
					icon: <FiUpload />,
					disabled: selected.length === 0,
				},
				{
					name: "Export To Excel",
					onClick: exportSelectedFormGroupsToExcel,
					icon: <FiUpload />,
					disabled: selected.length === 0,
				},
				{
					name: "Export Images",
					onClick: exportSelectedFormGroupImages,
					icon: <FiImage />,
					disabled: selected.length === 0,
				},
				{
					name: "Duplicate",
					onClick: duplicateSelectedFormGroups,
					icon: <FiCopy />,
					disabled: selected.length === 0,
				},
				{
					name: "Download",
					onClick: downloadSelectedFormGroups,
					icon: <FiDownload />,
					disabled: selected.length === 0,
				},
				{
					name: "Delete",
					onClick: () => setDeleteFormGroup(true),
					icon: <FiTrash2 />,
					disabled: selected.length === 0,
					variant: "destructive",
				},
			],
		};
	} else if (online) {
		optionsObj.Option = {
			icon: <FiMoreVertical />,
			dataTestId: "project-options",
			options: [
				{
					name: "Upload JSON",
					onClick: () => {
						setUploadJSONProjectOpen(!uploadJSONProjectOpen);
					},
					icon: <FiUploadCloud />,
				},

				// {
				// 	name: "Upload CSV",
				// 	onClick: () => {
				// 		setUploadCSVProjectOpen(!uploadCSVProjectOpen);
				// 	},
				// 	icon: <FiUpload />,
				// 	dataTestId: "csv-upload",
				// },
				{
					name: "Manage Project",
					onClick: () => {
						setEditProjectOpen(true);
					},
					icon: <FiEdit />,
					dataTestId: "edit-project",
				},
				{
					name: "Upload GIS CSV",
					onClick: () => {
						setUploadGISCsvProjectOpen(!uploadGISCsvProjectOpen);
					},
					icon: <FiMapPin />,
					disabled: selected.length === 0,
				},
				// {
				// 	name: "Delete Project",
				// 	onClick: () => {
				// 		setDeleteProjectOpen(true);
				// 	},
				// 	icon: <FiTrash2 />,
				// 	dataTestId: "delete-project",
				// 	variant: "destructive",
				// },
				{
					name: "Export",
					onClick: exportSelectedFormGroups,
					icon: <FiUpload />,
					disabled: selected.length === 0,
				},
				{
					name: "Export To Excel",
					onClick: exportSelectedFormGroupsToExcel,
					icon: <FiUpload />,
					disabled: selected.length === 0,
				},
				{
					name: "Export Images",
					onClick: exportSelectedFormGroupImages,
					icon: <FiImage />,
					disabled: selected.length === 0,
				},
				{
					name: "Duplicate",
					onClick: duplicateSelectedFormGroups,
					icon: <FiCopy />,
					disabled: selected.length === 0,
				},
				{
					name: "Download",
					onClick: downloadSelectedFormGroups,
					icon: <FiDownload />,
					disabled: selected.length === 0,
				},
				{
					name: "Delete",
					onClick: () => setDeleteFormGroup(true),
					icon: <FiTrash2 />,
					disabled: selected.length === 0,
					variant: "destructive",
				},
			],
		};
	}

	/**
	 * Downloads all selected formGroups and its child instances (NOT TEMPLATES) into a json file, named with their username and timestamp
	 */
	async function downloadSelectedFormGroups(): Promise<void> {
		try {
			setOpenDropdownId(null);
			setProgress({
				value: 0,
				variant: "info",
				message: "Retrieving selected form groups",
				title: "Downloading Form Groups",
				canClose: false,
				show: true,
			});
			const syncConfig: SyncConfig = {
				online,
				source: online ? "remote" : "local",
				destination: "json",
			};
			const jsonStructure = await downloadFormGroupJson(project, selected, syncConfig);
			setProgress({
				value: 50,
				variant: "info",
				message: "Preparing json backup",
				title: "Downloading Form Groups",
				canClose: false,
				show: true,
			});
			const stringifiedJson = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(jsonStructure))}`;
			const downloadAnchorNode = document.createElement("a");
			const date = new Date();
			downloadAnchorNode.setAttribute("href", stringifiedJson);
			const year = date.getFullYear().toString().slice(-2); // Last 2 digits of the year
			const month = (date.getMonth() + 1).toString().padStart(2, "0"); // The month with a leading zero if less than 2 characters
			const day = date.getDate().toString().padStart(2, "0"); // The day with a leading 0 if it's less than 2 characters
			const hour = date.getHours().toString().padStart(2, "0"); // The hour with a leading 0 if less than 2 characters
			const minute = date.getMinutes().toString().padStart(2, "0"); // The minutes with a leading 0 if less than 2 characters
			const seconds = date.getSeconds().toString().padStart(2, "0"); // The seconds with a leading 0 if less than 2 characters
			// const splitTime = date.toLocaleString("en-US", { hour12: false }).split(" ");//[date, time]
			// const dateString = splitTime[0].replaceAll(/[\\\/\:\*\?\"\<\>\|]/g, "-").replaceAll(",", "");
			// const timeString = splitTime[1].replaceAll(/:/g, "");
			const finalTime = `${hour}${minute}${seconds} ${month}-${day}-${year}`;
			downloadAnchorNode.setAttribute("download", `${finalTime}|${project.name}|${user?.username || "UnknownUser"}.json`);
			document.body.appendChild(downloadAnchorNode); // required for firefox
			downloadAnchorNode.click();
			downloadAnchorNode.remove();
			setProgress({
				value: 100,
				variant: "success",
				message: "Download complete",
				title: "Download Complete",
				timeout: 5000,
				canClose: true,
				show: true,
			});
		} catch (err) {
			if (err instanceof Error) {
				setProgress({
					value: 0,
					variant: "destructive",
					message: `Download failed. If this error persists, email devs@factorearth.com and mention which formGroups you want to download from which project. Error: ${err.message}`,
					title: "Error Downloading Form Groups",
					canClose: true,
					show: true,
				});
			}
			recordSentry({ error: err, msg: "Error downloading formGroups:", showModal: true });
		}
	}

	/**
	 * Gets the json for the selected formGroups, posts them to the junction, blobs the response, and downloads
	 * @author Eric Webb <ewebb@factorearth.com>
	 */
	async function exportSelectedFormGroups(): Promise<void> {
		try {
			setProgress({
				value: 33,
				variant: "info",
				message: "Processing your export",
				title: "Exporting Form Groups",
				canClose: false,
				show: true,
			});

			await trackUserBehaviorEvent("data_export", {
				formGroupIds: selected, // Array of form group IDs
				formGroupCount: selected.length,
				projectId: project?.id || "unknown",
				timestamp: new Date().toISOString(),
				online: online,
			});

			await callExportLambda({ projectId: project.id, formGroups: selected, userId: user?.id });
		} catch (err) {
			recordSentry({
				error: err,
				msg: `Failed to export the following formGroups in ${project.name}: ${formGroups
					.reduce((acc, fg) => {
						if (selected.includes(fg.id)) {
							acc.push(fg.name);
						}
						return acc;
					}, [] as string[])
					.join(", ")}`,
			});
		}
	}

	/**
	 * Gets the json for the selected formGroups, posts them to the junction, blobs the response, and downloads
	 */
	async function exportSelectedFormGroupsToExcel(): Promise<void> {
		try {
			setProgress({
				value: 33,
				variant: "info",
				message: "Processing your export",
				title: "Exporting Form Groups",
				canClose: false,
				show: true,
			});

			await trackUserBehaviorEvent("data_export", {
				formGroupIds: selected, // Array of form group IDs
				formGroupCount: selected.length,
				projectId: project?.id || "unknown",
				timestamp: new Date().toISOString(),
				online: online,
			});

			await callExportLambda({ projectId: project.id, formGroups: selected, userId: user?.id, toExcel: true });
		} catch (err) {
			recordSentry({
				error: err,
				msg: `Failed to export the following formGroups in ${project.name}: ${formGroups
					.reduce((acc, fg) => {
						if (selected.includes(fg.id)) {
							acc.push(fg.name);
						}
						return acc;
					}, [] as string[])
					.join(", ")}`,
			});
		}
	}

	/**
	 * Invokes the ExportImagesTest Lambda with the selected FormGroup IDs.
	 * On success, surfaces a presigned download link rather than auto-downloading,
	 * since image ZIPs can be very large.
	 */
	async function exportSelectedFormGroupImages(): Promise<void> {
		// Crawling progress: asymptotically approaches CRAWL_CEILING while the Lambda runs.
		// Formula: next = current + (ceiling - current) * 0.1 — decelerates naturally, never reaches ceiling.
		const CRAWL_CEILING = 88;
		let crawlValue = 5;

		setProgress({
			value: crawlValue as PercentValue,
			variant: "info",
			message: "Please be patient. Do not close or navigate away until your download is ready.",
			title: "Exporting Images",
			canClose: false,
			show: true,
		});

		const crawlInterval = setInterval(() => {
			crawlValue = crawlValue + (CRAWL_CEILING - crawlValue) * 0.1;
			setProgress({
				value: Math.round(crawlValue) as PercentValue,
				variant: "info",
				message: "Please be patient. Do not close or navigate away until your download is ready.",
				title: "Exporting Images",
				canClose: false,
				show: true,
			});
		}, 800);

		try {
			const { credentials } = await fetchAuthSession();
			if (!credentials) {
				throw new Error("No valid credentials found. Please sign in again.");
			}
			const lambdaClient = new LambdaClient({ region: import.meta.env.AMPLIFY_REGION ?? "us-west-1", credentials });
			const command = new InvokeCommand({
				FunctionName: `ExportImagesTest-${import.meta.env.VITE_APP_ENV !== "production" ? "staging" : "production"}`,
				Payload: new TextEncoder().encode(JSON.stringify({ formGroupIds: selected })),
			});

			const response = await lambdaClient.send(command);
			clearInterval(crawlInterval);

			if (response.FunctionError) {
				const rawPayload = response.Payload ? new TextDecoder().decode(response.Payload) : "";
				let functionErrMsg = "Lambda function error";
				try {
					const errPayload = JSON.parse(rawPayload);
					functionErrMsg = errPayload.errorMessage ?? functionErrMsg;
				} catch {
					/* ignore parse failure */
				}
				throw new Error(functionErrMsg);
			}

			if (!response.Payload) {
				throw new Error("Empty response from image export service.");
			}

			const responsePayload = JSON.parse(new TextDecoder().decode(response.Payload));

			if (response.StatusCode === 200 && responsePayload.success && responsePayload.presignedUrl) {
				const zipSizeBytes: number | undefined = responsePayload.stats?.zipSizeBytes;
				const zipSizeLabel = zipSizeBytes != null ? ` (${formatBytes(zipSizeBytes)})` : "";
				setProgress({
					value: 100,
					variant: "success",
					title: "Export Ready",
					message: "Your export is ready for download. Please click the link to download your exports.",
					downloadUrl: responsePayload.presignedUrl,
					downloadLabel: `Download ZIP${zipSizeLabel}`,
					canClose: true,
					show: true,
				});
			} else {
				setProgress({
					value: 0,
					variant: "destructive",
					message: `Image export failed: ${responsePayload.error ?? "Unknown error"}. If this persists, please report a bug.`,
					title: "Error Exporting Images",
					canClose: true,
					show: true,
				});
			}
		} catch (err) {
			clearInterval(crawlInterval);
			console.error("[exportImages] caught error:", err);
			setProgress({
				value: 0,
				variant: "destructive",
				message: `Image export failed. If this error persists, please report a bug.`,
				title: "Error Exporting Images",
				canClose: true,
				show: true,
			});
			recordSentry({ error: err, msg: "Error exporting images for formGroups", showModal: true });
		}
	}

	async function callExportLambda(payload: any): Promise<any> {
		const { credentials } = await fetchAuthSession();
		if (!credentials) {
			throw new Error("No valid credentials found. Please sign in again.");
		}

		const lambdaClient = new LambdaClient({
			region: import.meta.env.AMPLIFY_REGION ?? "us-west-1",
			credentials,
		});

		const command = new InvokeCommand({
			FunctionName: `ExportFormGroups-${import.meta.env.VITE_APP_ENV !== "production" ? "staging" : "production"}`, // Replace with your Lambda function name
			Payload: new TextEncoder().encode(JSON.stringify(payload)),
		});

		try {
			const response: InvokeCommandOutput = await lambdaClient.send(command);

			if (response.FunctionError) {
				const rawPayload = response.Payload ? new TextDecoder().decode(response.Payload) : "";
				let functionErrMsg = "Lambda function error";
				try {
					const errPayload = JSON.parse(rawPayload);
					functionErrMsg = errPayload.errorMessage ?? functionErrMsg;
				} catch {
					/* ignore parse failure */
				}
				throw new Error(functionErrMsg);
			}

			if (!response.Payload) {
				throw new Error("Empty response from export service.");
			}

			const responsePayload = JSON.parse(new TextDecoder().decode(response.Payload));

			if (response.StatusCode === 200 && response.Payload && responsePayload.signedUrl) {
				// Now trigger a callback?
				setProgress({
					value: 80,
					variant: "info",
					message: "Preparing to download your export",
					title: "Exporting Form Groups",
					canClose: false,
					show: true,
				});
				const downloadAnchorNode = document.createElement("a");

				downloadAnchorNode.setAttribute("href", responsePayload.signedUrl);
				downloadAnchorNode.setAttribute("download", `${project.name}.zip`);
				document.body.appendChild(downloadAnchorNode); // required for firefox
				downloadAnchorNode.click();
				downloadAnchorNode.remove();
				setSelected([]);
				setProgress({
					value: 100,
					variant: "success",
					message: "Successfully downloaded your export",
					title: "Successfully Exported Form Groups",
					timeout: 5000,
					canClose: true,
					show: true,
				});
			} else {
				setProgress({
					value: 0,
					variant: "destructive",
					message: `Export failed. Error message: ${responsePayload.message}-${responsePayload.error}. If this error persists, please report the issue by clicking "report a bug" in the navigation menu`,
					title: "Error Exporting Form Groups",
					canClose: true,
					show: true,
				});
				throw new Error(`Lambda invocation failed with status code: ${responsePayload.error}`);
			}
		} catch (error) {
			console.error("Error invoking Lambda:", error);
			throw error;
		}
		return true;
	}

	async function duplicateSelectedFormGroups(): Promise<void> {
		if (!duplicateInProgress) {
			setDuplicateInProgress(true);
			try {
				setProgress({
					value: 0,
					variant: "info",
					message: `Duplicating ${selected.length} Form Groups`,
					title: "Duplicating Form Groups",
					canClose: false,
					show: true,
				});
				const duplicateResult = await duplicateFormGroups(selected, online);
				const syncConfig: SyncConfig = {
					destination: online ? "remote" : "local",
					source: online ? "remote" : "local",
					online,
				};
				setProgress({
					value: 33,
					variant: "info",
					message: "Saving duplicate Form Groups to the database",
					title: "Duplicating Form Groups",
					canClose: false,
					show: true,
				});
				await saveJsonDirectlyToDb(duplicateResult.json, syncConfig);
				setProgress({
					value: 66,
					variant: "info",
					message: "Refreshing FormGroup list",
					title: "Duplicating Form Groups",
					canClose: false,
					show: true,
				});
				await fetchFormGroupsForState(duplicateResult.formGroups);
				setProgress({
					value: 100,
					variant: "success",
					message: "Form Groups Successfully Duplicated",
					title: "Form Groups Successfully Duplicated",
					timeout: 5000,
					canClose: true,
					show: true,
				});
				setOpenDropdownId(null);
			} catch (err) {
				recordSentry({ error: err, msg: "Error duplicating formGroups" });
			}
			setDuplicateInProgress(false);
		}
	}

	/**
	 * Delete the project in its entirety
	 */
	async function handleDeleteProjectClick(): Promise<void> {
		navigate("/");
		await deleteProject(project);
	}

	/**
	 * Delete all the selected form groups button click handler
	 */
	async function handleDeleteSelected(): Promise<void> {
		await deleteSelectedFormGroups(selected);
		const selectedString = selected.join(",");
		const forceUpdateDataString = `DELETESELECTED_FORMGROUP_${selectedString}`;
		const updatedProject = {
			...project,
			forceUpdateData: forceUpdateDataString,
		};

		if (online) await handleProjectUpdate(updatedProject);

		let page = (formGroups.length - selected.length) / pageSize;
		if (page > 1 && pageIndex > page) {
			setPageIndex(pageIndex - 1);
		}
		setSelected([]);
		if (!online) window.location.reload();
	}

	/**
	 * When the input changes, fires this function
	 * @author Eric Webb <ewebb@factorearth.com>
	 * @param e The input change event
	 */
	async function uploadFormGroups(file: File | null): Promise<void> {
		if (file && user) {
			const fileReader = new FileReader();
			await new Promise<void>((resolve, reject) => {
				fileReader.readAsText(file, "UTF-8");
				fileReader.onload = async function (e) {
					if (e.target) {
						const jsonString = e.target.result;
						if (typeof jsonString === "string") {
							let json;
							try {
								json = JSON.parse(jsonString);
							} catch (error) {
								reject("INVALID JSON UPLOADED");
								recordSentry({ error, msg: "Invalid Json Uploaded:", showModal: true });
								return;
							}
							if (!json.version) {
								reject("OLD JSON");
								setProgress({
									value: 0,
									variant: "destructive",
									message:
										"Deprecated Json Structure. If you need to restore this json, please reach out to the dev team at devs@factorearth.com",
									title: "Error Uploading Form Groups",
									canClose: true,
									show: true,
								});
								return;
							}
							/**
							 * Given a formGroup id, returns whether or not the formGroups array contains a formGroup with that id. This is so we can find out if a json uploaded formGroup needs
							 * merged or if it needs to just be created
							 * @author Eric Webb <ewebb@factorearth.com>
							 * @param {Object} jsonFG The formGroup we're searching for
							 * @returns True or false, depending on if the formGroup array contains a formGroup with the id we passed
							 */
							function checker(jsonFG: FormGroupForResolver) {
								return formGroups.some((fg: FormGroup) => fg.id === jsonFG.id);
							}

							function fixProjectIds(json: any, projectId: string): any {
								const jsonCopy = { ...json };
								if (jsonCopy.formGroups) {
									jsonCopy.formGroups = jsonCopy.formGroups.map((fg: any) => {
										const fgCopy = { ...fg };
										fgCopy.projectId = projectId;
										return fgCopy;
									});
								}
								return jsonCopy;
							}
							try {
								// if (json.projects[0].id === project.id) {
								// Since things are being uploaded into a project, the project id needs to be project.id for every formGroup
								const fixedJson = fixProjectIds(json, project.id);
								const donorData = prepJsonForMerge(fixedJson);
								const formGroupArray = fixedJson.formGroups;
								const [both] = divideInto2Arrays(formGroupArray, checker);
								const ids = formGroups.map((fg: FormGroupForResolver) => fg.id);
								const syncConfig: SyncConfig = {
									online,
									source: "json",
									destination: online ? "remote" : "local",
								};
								// receiver data ids actually need to go off of the component prop formGroups, not just formgroups that
								// are included in the donor data. (because of templates!!)
								const receiverData = await prepDbForMerge(user, syncConfig, ids, project);
								// Enhance receiverData with all available form templates (only if newer than donorData)
								await addAllFormTemplatesToMergeData(receiverData, donorData, syncConfig.destination === "remote");
								await mergeAndSave(donorData, receiverData, syncConfig, { username: user?.username });
								await getProject(project.id);
								resolve();
							} catch (err) {
								reject("ERROR UPLOADING FORMGROUPS");
								recordSentry({ error: err, msg: "Error Uploading FormGroups:", showModal: true });
							}
						}
					}
				};
			});
		}
	}
	/**
	 * When the input changes, fires this function for csvs
	 * @author Eric Webb and Robbie <ewebb@factorearth.com>, <rmartin@factorearth.com>
	 * @param {} e The input change event
	 */
	async function uploadFormGroupsCsv(file: File | null): Promise<void> {
		if (file) {
			const fileReader = new FileReader();
			fileReader.readAsText(file, "UTF-8");
			fileReader.onload = async function (e) {
				if (e.target && typeof e.target.result === "string") {
					const csvString = e.target.result;
					await handleUploadFromCsv(csvString, project);
					await getProject(project.id);
				}
			};
		}
	}

	async function handleGISCsvUploadClick(file: File | null): Promise<void> {
		if (file) {
			// takes the file and calls the callback (for specific future UI needs potentially)
			handleGISCsvUpload(file);
		}
	}

	return (
		<>
			<DownloadImagesModal color={color} />
			<div className={styles.searchDiv} data-test="sync-running" data-sync-running={syncRunning ? "true" : "false"}>
				<div className={styles.searchWrapper}>
					<TextFieldWrapper
						type="text"
						onChange={(e) => setQuery(e.target.value)}
						placeholder="Search"
						colorPalette={color}
						value={query}
						id="search-bar-top-formgrouplist"
						name="search-bar-top-formgrouplist"
					/>
				</div>
				<div style={{ display: "flex", gap: "24px", alignItems: "center", justifyContent: "center" }}>
					<Button
						variant="filled"
						icon={<FiPlus />}
						colorPalette={color}
						onClick={handleCreateNewClick}
						label="Folder"
						data-test="new-form-group-button"
					/>
					<OptionsDropdown colorPalette={color} optionsObj={optionsObj} />
				</div>
			</div>
			{uploadJSONProjectOpen && (
				<UploadFileButton
					accept="application/JSON"
					color={color}
					title="Upload JSON"
					onSubmit={uploadFormGroups}
					open={uploadJSONProjectOpen}
					setOpen={setUploadJSONProjectOpen}
				/>
			)}
			{uploadGISCsvProjectOpen && (
				<CsvUploadModal
					accept=".csv"
					color={color}
					onFileUpload={handleGISCsvUploadClick}
					onApplyImport={handleApplyCsvImport}
					isOpen={uploadGISCsvProjectOpen}
					setOpen={setUploadGISCsvProjectOpen}
					csvPreview={csvPreview}
				/>
			)}
			{uploadCSVProjectOpen && (
				<UploadFileButton
					accept=".csv"
					color={color}
					title="Upload CSV"
					onSubmit={uploadFormGroupsCsv}
					open={uploadCSVProjectOpen}
					setOpen={setUploadCSVProjectOpen}
				/>
			)}
			<DeleteProjectModal
				color={color}
				handleDeleteProjectClick={handleDeleteProjectClick}
				project={project}
				deleteProjectOpen={deleteProjectOpen}
				setDeleteProjectOpen={setDeleteProjectOpen}
				setEditProjectOpen={setEditProjectOpen}
			/>
			<EditProjectModal
				online={online}
				colorPalette={color}
				project={project}
				editProjectOpen={editProjectOpen}
				setEditProjectOpen={setEditProjectOpen}
				formTemplates={formTemplates}
				updateProject={updateProject}
				setDeleteProjectOpen={setDeleteProjectOpen}
				deleteProjectOpen={deleteProjectOpen}
			/>
			<DeleteSelectedFormGroupsButton
				color={color}
				handleDeleteSelected={handleDeleteSelected}
				selected={selected.length}
				deleteFormGroup={deleteFormGroup}
				setDeleteFormGroup={setDeleteFormGroup}
			/>
		</>
	);
}

interface DeleteSelectedFormGroupsButtonProps {
	color: Colors;
	handleDeleteSelected: () => Promise<void>;
	selected: number;
	deleteFormGroup: boolean;
	setDeleteFormGroup: React.Dispatch<React.SetStateAction<boolean>>;
}

function DeleteSelectedFormGroupsButton(props: DeleteSelectedFormGroupsButtonProps) {
	const { color, handleDeleteSelected, selected, deleteFormGroup, setDeleteFormGroup } = props;

	async function submit() {
		setDeleteFormGroup(false);
		handleDeleteSelected();
	}

	return (
		<>
			<ConfirmationModal
				colorPalette={color}
				setOpen={setDeleteFormGroup}
				confirmTitle={
					<Typography variant="Heading 3" textColor={color.text.primary}>
						Delete Form Groups
					</Typography>
				}
				details={`Please confirm that you want to delete the selected folder(s) by typing DELETE.\n\nThis action is irreversible.`}
				handleSubmit={submit}
				open={deleteFormGroup}
				canClose
				confirmationText="DELETE"
				timeout={5000}
			/>
		</>
	);
}

interface DeleteProjectModalProps {
	color: Colors;
	handleDeleteProjectClick: () => Promise<void>;
	project: Project;
	setDeleteProjectOpen: React.Dispatch<React.SetStateAction<boolean>>;
	deleteProjectOpen: boolean;
	setEditProjectOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function DeleteProjectModal(props: DeleteProjectModalProps) {
	const { color, handleDeleteProjectClick, project, setDeleteProjectOpen, deleteProjectOpen, setEditProjectOpen } = props;

	const [input, setInput] = useState("");

	async function submit() {
		if (input === project.name) {
			setDeleteProjectOpen(false);
			handleDeleteProjectClick();
		}
	}

	function handleCancel() {
		setDeleteProjectOpen(false);
		setEditProjectOpen(true);
	}

	return (
		<>
			<Modal open={deleteProjectOpen} colorPalette={color} setOpen={setDeleteProjectOpen}>
				<DialogContainer>
					<ModalTitleContainer>
						<ModalTitle>Delete Project</ModalTitle>
						<FiX onClick={handleCancel} size={24} />
					</ModalTitleContainer>
					<ModelSubHeading>This will delete {project.name}</ModelSubHeading>
					<ModalInputBox>
						<ModalHeading>Please enter "{project.name}" to confirm</ModalHeading>
						<TextFieldWrapper colorPalette={color} placeholder="" value={input} onChange={(e) => setInput(e.target.value)} />
					</ModalInputBox>
					<ModalFooter>
						<Button colorPalette={color} variant="filled" label="Delete" onClick={submit} />
						<Button colorPalette={color} variant="destructive" label="Cancel" onClick={handleCancel} />
					</ModalFooter>
				</DialogContainer>
			</Modal>
		</>
	);
}

interface EditProjectModalProps {
	colorPalette: Colors;
	project: Project;
	setEditProjectOpen: React.Dispatch<React.SetStateAction<boolean>>;
	editProjectOpen: boolean;
	online: boolean;
	formTemplates: FormTemplateForResolver[];
	updateProject?: (project: Project) => Promise<void>;
	setDeleteProjectOpen: React.Dispatch<React.SetStateAction<boolean>>;
	deleteProjectOpen: boolean;
}

function EditProjectModal(props: EditProjectModalProps) {
	const { colorPalette, project, setEditProjectOpen, editProjectOpen, online, updateProject, formTemplates, setDeleteProjectOpen, deleteProjectOpen } = props;

	const [input, setInput] = useState("");
	const [openSelectFormTemplates, setOpenSelectFormTemplates] = useState(false);
	const [selectedFormTemplateIds, setSelectedFormTemplateIds] = useState<string[]>([]);
	const [deselectedFormTemplateIds, setDeselectedFormTemplateIds] = useState<string[]>([]);

	useEffect(() => {
		if (project && project.syncFormTemplates) {
			const formTemplatesToSync = project.syncFormTemplates;
			setSelectedFormTemplateIds(formTemplatesToSync || []);
		}
	}, [project, openSelectFormTemplates]);

	const handleClose = () => {
		setEditProjectOpen(false);
		setOpenSelectFormTemplates(false);
		setSelectedFormTemplateIds(project?.syncFormTemplates || []);
	};

	async function submit() {
		const updatedProject = { ...project, name: input || project.name };
		delete updatedProject.offline;
		if (updateProject) await updateProject(updatedProject);
		await handleSetOrRemoveAutoSyncFormTemplates(deselectedFormTemplateIds, updatedProject, "remove");
		await handleSetOrRemoveAutoSyncFormTemplates(selectedFormTemplateIds, updatedProject, "set");
		handleClose();
		updateRecentProjects({ id: updatedProject.id, name: updatedProject.name, description: updatedProject.description } as RecentProject);
		window.location.reload();
	}
	const handleSelectFormTemplates = () => {
		setEditProjectOpen(false);
		setOpenSelectFormTemplates(true);
	};

	function handleDeleteConfirmationOpen() {
		setEditProjectOpen(false);
		setDeleteProjectOpen(true);
	}

	return (
		<>
			{openSelectFormTemplates ? (
				<SelectAutoSyncTemplates
					formTemplates={formTemplates}
					openSelectFormTemplates={openSelectFormTemplates}
					setOpenSelectFormTemplates={setOpenSelectFormTemplates}
					handleClose={handleClose}
					setSelectedFormTemplateIds={setSelectedFormTemplateIds}
					selectedFormTemplateIds={selectedFormTemplateIds}
					editSubmit={submit}
					setDeselectedFormTemplateIds={setDeselectedFormTemplateIds}
				/>
			) : (
				<Modal open={editProjectOpen} colorPalette={colorPalette} setOpen={setEditProjectOpen}>
					<DialogContainer>
						<ModalTitleContainer>
							<ModalTitle>Manage Project</ModalTitle>
							<FiX onClick={() => setEditProjectOpen(false)} size={24} />
						</ModalTitleContainer>
						<ModalInputBox>
							<ModalHeading>Edit Project Name:</ModalHeading>
							<TextFieldWrapper
								data-test="edit-project-input"
								colorPalette={colorPalette}
								placeholder={project.name}
								value={input}
								onChange={(e) => setInput(e.target.value)}
							/>
						</ModalInputBox>
						<ModalFooter>
							{online ? (
								<Button
									type="button"
									data-test={"select-templates-for-auto-sync-edit"}
									colorPalette={colorPalette}
									onClick={handleSelectFormTemplates}
									variant="filled"
									label="Select Form Templates"
									icon={<FiChevronRight />}
								/>
							) : (
								<>
									<Button colorPalette={colorPalette} variant="filled" label="Save" onClick={submit} />
									<Button colorPalette={colorPalette} variant="destructive" label="Cancel" onClick={() => setEditProjectOpen(false)} />
								</>
							)}

							<Button
								type="button"
								name="Delete Project"
								data-test={"delete-project"}
								colorPalette={colorPalette}
								onClick={handleDeleteConfirmationOpen}
								variant="destructive"
								label="Delete Project"
								icon={<FiTrash2 />}
							/>
						</ModalFooter>
					</DialogContainer>
				</Modal>
			)}
		</>
	);
}

interface CsvUploadModalProps {
	isOpen: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	accept: string;
	onFileUpload: (file: File) => Promise<void>;
	onApplyImport: (preview: ChangePreview | null) => Promise<void>;
	csvPreview: ChangePreview | null;
	color: Colors;
}

export default FormGroupListOptions;
